/**
 * An absent value, written IN WORDS — `docs/adr/9061`.
 *
 * A client read the Simulate table's «—¹» and «—²» as the numbers −1 and −2. A dash is not a word and
 * a superscript is not a footnote to a reader who has never been told: the owner's rule (2026-10-13)
 * is that an absence says what it is, in the cell, in the language of the screen — «Nessuna riga»,
 * «Non confrontabile». No dash, no marker.
 *
 * ONE PLACE draws it, so that every cell that can be absent reads the same way:
 *
 *   - the label (`absent.labelKey`) is the visible text, in secondary ink, on one line;
 *   - a long label is cut at 24 characters of the cell and its full words, with the reason, ride in
 *     `title`;
 *   - the reason, where there is one, is stated ONCE per table by `AbsenceNotes` as
 *     «<label>: <reason>», and the cell points at it with `aria-describedby`; outside a table that
 *     states reasons it stays as a badge, because a reason with nowhere else to go must not be dropped.
 *
 * The ghost silhouette (ADR 9013) is NOT an absence and is not drawn here: it keeps `—`.
 */

import { useTranslation } from 'react-i18next';

import { useAbsenceNote } from './absence-notes';
import { Badge } from './display';
import type { AbsentMeaning } from './figures';

export interface AbsentWordsProps {
  readonly absent: AbsentMeaning;
  /** Classes for the words themselves — ink and size; the width cap and the one line are fixed. */
  readonly className?: string;
}

export function AbsentWords({ absent, className = '' }: AbsentWordsProps): React.JSX.Element {
  const { t } = useTranslation();
  const note = useAbsenceNote(absent.labelKey, absent.reasonKey);
  const label = t(absent.labelKey);
  // The full words, for a label the cell had to cut: the label alone, or label and reason.
  const title = absent.reasonKey === undefined ? label : t('table.absence.note', { label, reason: t(absent.reasonKey) });
  return (
    <span className="inline-flex items-baseline gap-1">
      <span
        title={title}
        className={`min-w-0 max-w-[24ch] truncate whitespace-nowrap${className === '' ? '' : ` ${className}`}`}
        {...(note === null || note === undefined ? {} : { 'aria-describedby': note.id })}
      >
        {label}
      </span>
      {absent.reasonKey === undefined || note !== undefined ? null : <Badge labelKey={absent.reasonKey} />}
    </span>
  );
}
